"use client";

import { useState } from "react";
import { GripVertical, Plus, SlidersHorizontal, Trash2 } from "lucide-react";

import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import {
    ChecklistAppearance,
    ChecklistBlock,
    ChecklistItem,
} from "@/lib/utils/lomdaV3/types";
import { useLomdaV3Editor } from "../../store/useLomdaV3Editor";
import { useResolvedGridGap } from "../../hooks/useGridGap";
import {
    CHECKLIST_CONFIG,
    resolveChecklistAppearance,
    createChecklistItem,
} from "../../lib/blockRegistry";
import {
    AppearanceDivider,
    AppearanceReset,
    AppearanceRow,
    ColorChipField,
    ToolbarDivider,
    PopoverTitle,
    WidthControl,
    chromeBtn,
    chromeBtnOpen,
} from "./blockAppearance";

export default function ChecklistSettings({ block }: { block: ChecklistBlock }) {
    const replaceBlockData = useLomdaV3Editor((s) => s.replaceBlockData);
    const setBlockGridGap = useLomdaV3Editor((s) => s.setBlockGridGap);
    const gridGap = useResolvedGridGap(block.id);

    const { items } = block.data;
    const theme = useLomdaV3Editor((s) => s.metadata.theme);
    const appearance = resolveChecklistAppearance(block.data.appearance, theme);
    const update = (patch: Partial<ChecklistBlock["data"]>) =>
        replaceBlockData(block.id, { ...block.data, ...patch });
    const setItems = (next: ChecklistItem[]) => update({ items: next });

    return (
        <div className="flex items-center gap-3">
            <div className="flex items-center gap-3">
                <span className="shrink-0 text-sm !font-semibold text-foreground">
                    {t("lomda-v3-editor.interactive.checklist")}
                </span>
                <WidthControl blockId={block.id} />
                <div className="flex shrink-0 items-center gap-2">
                    <span className="whitespace-nowrap text-sm text-foreground">
                        {t("lomda-v3-editor.settings.grid-gap")}
                    </span>
                    <div className="flex h-9 w-16 items-center rounded-lg border px-2 text-sm text-foreground">
                        <input
                            type="number"
                            min={0}
                            value={gridGap}
                            onChange={(e) =>
                                setBlockGridGap(
                                    block.id,
                                    Math.max(0, parseInt(e.target.value, 10) || 0)
                                )
                            }
                            className="w-full bg-transparent text-center outline-none"
                        />
                    </div>
                </div>
            </div>

            <ToolbarDivider />

            <div className="flex items-center gap-3">
                <AppearancePopover
                    appearance={appearance}
                    onChange={(patch) =>
                        update({ appearance: { ...appearance, ...patch } })
                    }
                    onReset={() => update({ appearance: undefined })}
                />
                <ManageItemsPopover items={items} onChange={setItems} />
            </div>
        </div>
    );
}

function ManageItemsPopover({
    items,
    onChange,
}: {
    items: ChecklistItem[];
    onChange: (items: ChecklistItem[]) => void;
}) {
    const [dragIndex, setDragIndex] = useState<number | null>(null);
    const move = (from: number, to: number) => {
        if (from === to) return;
        const next = [...items];
        const [item] = next.splice(from, 1);
        next.splice(to, 0, item);
        onChange(next);
    };
    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    className={cn(chromeBtn, chromeBtnOpen)}
                >
                    <GripVertical className="size-5" />
                    {t("lomda-v3-editor.checklist.manage-items")}
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="end"
                className="w-[352px] rounded-[20px] border border-border bg-card p-4 shadow-md"
            >
                <div className="flex flex-col gap-4">
                    <PopoverTitle>
                        {t("lomda-v3-editor.checklist.manage-items")}
                    </PopoverTitle>
                    {items.map((it, i) => (
                        <div
                            key={it.id}
                            draggable
                            onDragStart={() => setDragIndex(i)}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={() => {
                                if (dragIndex !== null) move(dragIndex, i);
                                setDragIndex(null);
                            }}
                            className="flex items-center gap-2.5"
                        >
                            <GripVertical className="size-5 shrink-0 cursor-grab text-muted-foreground" />
                            <span className="text-sm text-primary">{i + 1}</span>
                            <input
                                value={it.text.replace(/<[^>]*>/g, "")}
                                onChange={(e) =>
                                    onChange(
                                        items.map((x) =>
                                            x.id === it.id
                                                ? { ...x, text: e.target.value }
                                                : x
                                        )
                                    )
                                }
                                placeholder={t(
                                    "lomda-v3-editor.checklist.item-placeholder"
                                )}
                                className="min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none"
                            />
                            <button
                                type="button"
                                disabled={items.length <= 1}
                                onClick={() =>
                                    onChange(items.filter((x) => x.id !== it.id))
                                }
                                className="shrink-0 text-muted-foreground transition hover:text-destructive disabled:opacity-30"
                            >
                                <Trash2 className="size-5" />
                            </button>
                        </div>
                    ))}
                    <span className="h-px w-full bg-muted" />
                    <button
                        type="button"
                        onClick={() =>
                            onChange([...items, createChecklistItem()])
                        }
                        className={cn(chromeBtn, "h-10 self-start")}
                    >
                        <Plus className="size-5" />
                        {t("lomda-v3-editor.checklist.add")}
                    </button>
                </div>
            </PopoverContent>
        </Popover>
    );
}

function AppearancePopover({
    appearance,
    onChange,
    onReset,
}: {
    appearance: ChecklistAppearance;
    onChange: (patch: Partial<ChecklistAppearance>) => void;
    onReset: () => void;
}) {
    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    className={cn(chromeBtn, chromeBtnOpen)}
                >
                    <SlidersHorizontal className="size-5" />
                    {t("lomda-v3-editor.checklist.appearance")}
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                className="w-[320px] rounded-[20px] border border-border bg-card p-4 shadow-md"
            >
                <div className="flex flex-col gap-4">
                    <PopoverTitle>
                        {t("lomda-v3-editor.checklist.appearance")}
                    </PopoverTitle>
                    {CHECKLIST_CONFIG.appearanceGroups.map((group, gi) => (
                        <div key={gi} className="flex flex-col gap-4">
                            {gi > 0 && <AppearanceDivider />}
                            {group.map((field) => {
                                const apKey =
                                    field.key as keyof ChecklistAppearance;
                                const value = appearance[apKey];
                                return (
                                    <AppearanceRow
                                        key={field.key}
                                        label={t(field.labelKey)}
                                    >
                                        {field.control === "borderWidth" ? (
                                            <div className="flex h-9 w-14 items-center justify-center rounded-lg border border-border bg-card px-2 text-sm text-foreground">
                                                <input
                                                    type="number"
                                                    min={0}
                                                    max={8}
                                                    value={Number(value)}
                                                    onChange={(e) =>
                                                        onChange({
                                                            [apKey]: Math.max(
                                                                0,
                                                                parseInt(
                                                                    e.target
                                                                        .value,
                                                                    10
                                                                ) || 0
                                                            ),
                                                        })
                                                    }
                                                    className="w-6 bg-transparent text-center outline-none"
                                                />
                                                <span className="text-muted-foreground">
                                                    px
                                                </span>
                                            </div>
                                        ) : (
                                            <ColorChipField
                                                value={String(value)}
                                                onChange={(c) =>
                                                    onChange({ [apKey]: c })
                                                }
                                            />
                                        )}
                                    </AppearanceRow>
                                );
                            })}
                        </div>
                    ))}
                    <AppearanceReset
                        onReset={onReset}
                    />
                </div>
            </PopoverContent>
        </Popover>
    );
}
